import React from 'react';
import { Box, IconButton, SvgIcon, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EXTERNAL_LINKS } from '../../constants/config';

/**
 * Контакты плавающими виджетами у левого края — видны на любой прокрутке.
 *
 * Пришло на смену блоку «Мы в социальных сетях» в подвале главной: там это была
 * сетка 2×2 из карточек по 320px, которую видели только те, кто долистал до конца.
 *
 * Стиль намеренно повторяет `BackToTopButton` (тёмный круг, тонкая рамка, свечение
 * на наведении) — это два одинаковых по смыслу плавающих элемента на одной странице,
 * и разъезжаться им нельзя. Отличий два: подсветка берётся из фирменного цвета площадки,
 * а не из красного акцента, и кружки крупнее (62px против 52px у «наверх») — рейл
 * работает точкой входа в соцсети, его должно быть видно боковым зрением.
 *
 * Порог — 1200px, и он выбран замером, а не на глаз: на 900px колонка виджетов
 * ложится на текст экономического блока и «Базы знаний» (контент там доходит до
 * левого края), на 1200px пересечений уже нет ни на одной высоте прокрутки.
 * Ниже порога контакты живут строкой в подвале (`HomeFooter`).
 */

const DiscordIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </SvgIcon>
);

const TelegramIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </SvgIcon>
);

/**
 * Знак проекта вместо логотипа площадки — для чата в телеграме.
 *
 * Берём готовый favicon (`android-chrome-192x192.png`, не 32px: на retina мелкий замылится),
 * а не рисуем ещё один SVG: знак растровый, с текстурой, повторять его контурами бессмысленно.
 * Отсюда единственное отличие от соседей — это `<img>`, а не `<SvgIcon>`, поэтому серый вид и
 * возврат цвета на наведении делает `filter` в стилях кнопки, а не `color`, как у остальных.
 */
const OdiumMarkIcon = () => (
  <Box component="img" src="/android-chrome-192x192.png" alt="" aria-hidden />
);

const YouTubeIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </SvgIcon>
);

export interface SocialLink {
  id: string;
  icon: React.ReactNode;
  /** Ключ подписи — она же подсказка на наведении */
  textKey: string;
  link: string;
  color: string;
}

export const SOCIAL_LINKS: SocialLink[] = [
  { id: 'discord', icon: <DiscordIcon />, textKey: 'footer.social.discord.text', link: EXTERNAL_LINKS.DISCORD, color: '#5865F2' },
  // Чат — под знаком проекта, а не под логотипом телеграма: двух одинаковых «самолётиков»
  // подряд в рейле было не различить. Подсветка — фирменный оранжевый, взят пипеткой из самого
  // знака (самый насыщенный пиксель favicon — #e94f00), а не телеграмовский синий.
  { id: 'telegram-chat', icon: <OdiumMarkIcon />, textKey: 'footer.social.telegramChat.text', link: EXTERNAL_LINKS.TELEGRAM_CHAT, color: '#e94f00' },
  { id: 'youtube', icon: <YouTubeIcon />, textKey: 'footer.social.youtube.text', link: EXTERNAL_LINKS.YOUTUBE, color: '#FF0000' },
  { id: 'telegram-news', icon: <TelegramIcon />, textKey: 'footer.social.telegram.text', link: EXTERNAL_LINKS.TELEGRAM_NEWS, color: '#0088cc' },
];

/** Кнопка-виджет. Вынесена, чтобы подвал на мобиле рисовал те же кружки, а не свои. */
export const SocialButton: React.FC<{ item: SocialLink; size?: number }> = ({ item, size = 48 }) => {
  const { t } = useTranslation('home');
  // Подсказка и подсветка — только на широком экране, где живёт рейл.
  //
  // Условие именно по ШИРИНЕ, а не по `(hover: hover)`: в эмуляторе устройства и на
  // ноутбуках с сенсорным экраном мышь никуда не девается, `(hover: hover)` остаётся
  // истинным — и подсказка вылезает на «телефоне». А в строке подвала она не нужна в
  // любом случае: там кнопки крупные и подписаны иконкой.
  const theme = useTheme();
  const canHover = useMediaQuery(theme.breakpoints.up('lg'));

  const button = (
      <IconButton
        component="a"
        href={item.link}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={item.id}
        sx={{
          width: size,
          height: size,
          background: 'rgba(0, 0, 0, 0.6)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          color: 'rgba(255, 255, 255, 0.75)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          transition: 'all 0.25s ease',
          '& svg': { fontSize: size * 0.5 },
          // Растровый знак проекта живёт по тем же правилам, что и SVG-соседи: тот же размер,
          // в покое — серый и приглушённый до той же яркости, что даёт им color: 75% белого.
          '& img': {
            width: size * 0.5,
            height: size * 0.5,
            filter: 'grayscale(1) brightness(1.35)',
            opacity: 0.75,
            transition: 'all 0.25s ease',
          },
          // Подсветка — везде, где есть курсор (в том числе на мобильной ширине с мышью).
          // Порог по ширине тут не нужен: мешала не подсветка, а всплывающий текст.
          // На настоящем тач-экране `(hover: hover)` ложно, и подсветка не залипает после тапа.
          '@media (hover: hover)': {
            '&:hover': {
              background: `${item.color}26`,
              borderColor: item.color,
              color: item.color,
              boxShadow: `0 0 20px ${item.color}59, 0 8px 24px rgba(0,0,0,0.5)`,
              transform: 'translateX(3px)',
              // Знаку проекта возвращаем его собственные цвета — это его версия того, как
              // соседи на наведении перекрашиваются в цвет своей площадки.
              '& img': { filter: 'none', opacity: 1 },
            },
          },
        }}
      >
        {item.icon}
      </IconButton>
  );

  if (!canHover) return button;
  return (
    <Tooltip title={t(item.textKey)} placement="right" arrow>
      {button}
    </Tooltip>
  );
};

const SocialRail: React.FC = () => (
  <Box
    sx={{
      position: 'fixed',
      left: 24,
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 1200,
      display: { xs: 'none', lg: 'flex' },
      flexDirection: 'column',
      gap: 1.25,
    }}
  >
    {SOCIAL_LINKS.map((item) => (
      <SocialButton key={item.id} item={item} size={62} />
    ))}
  </Box>
);

export default SocialRail;
